import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

export interface DownloadProgress {
  id: string;
  deviceId: string;
  deviceName: string;
  status: 'initiated' | 'in_progress' | 'completed' | 'failed';
  progress: number; // 0-100
  totalFiles: number;
  downloadedFiles: number;
  currentFile?: string;
  startTime: Date;
  estimatedCompletion?: Date;
  error?: string;
}

interface DownloadProgressContextType {
  activeDownloads: DownloadProgress[];
  addDownload: (deviceId: string, deviceName: string) => string;
  updateProgress: (id: string, updates: Partial<DownloadProgress>) => void;
  completeDownload: (id: string) => void;
  failDownload: (id: string, error: string) => void;
  removeDownload: (id: string) => void;
  clearAllDownloads: () => void;
  hasActiveDownloads: boolean;
}

const DownloadProgressContext = createContext<DownloadProgressContextType | undefined>(undefined);

export const useDownloadProgress = () => {
  const context = useContext(DownloadProgressContext);
  if (!context) {
    throw new Error('useDownloadProgress must be used within a DownloadProgressProvider');
  }
  return context;
};

interface DownloadProgressProviderProps {
  children: ReactNode;
}

export const DownloadProgressProvider: React.FC<DownloadProgressProviderProps> = ({ children }) => {
  const [activeDownloads, setActiveDownloads] = useState<DownloadProgress[]>([]);

  const addDownload = useCallback((deviceId: string, deviceName: string): string => {
    const id = `download_${Date.now()}_${Math.random().toString(36).substr(2, 50)}`;
    const newDownload: DownloadProgress = {
      id,
      deviceId,
      deviceName,
      status: 'initiated',
      progress: 0,
      totalFiles: 0,
      downloadedFiles: 0,
      startTime: new Date(),
    };

    setActiveDownloads(prev => [...prev, newDownload]);
    return id;
  }, []);

  const updateProgress = useCallback((id: string, updates: Partial<DownloadProgress>) => {
    setActiveDownloads(prev => {
      // Always create a new array to ensure React detects the change
      const newDownloads = prev.map(download => {
        if (download.id === id) {
          // Create a new object with merged updates
          const updatedDownload = {
            ...download,
            ...updates,
            // Ensure status is set, defaulting to 'in_progress' if not provided
            status: updates.status || download.status || 'in_progress'
          };
          return updatedDownload;
        }
        return download;
      });
      return newDownloads;
    });
  }, []);

  const completeDownload = useCallback((id: string) => {
    setActiveDownloads(prev => 
      prev.map(download => 
        download.id === id 
          ? { 
              ...download, 
              status: 'completed', 
              progress: 100,
              downloadedFiles: download.totalFiles || 1
            }
          : download
      )
    );
  }, []);

  const failDownload = useCallback((id: string, error: string) => {
    setActiveDownloads(prev => 
      prev.map(download => 
        download.id === id 
          ? { ...download, status: 'failed', error }
          : download
      )
    );
  }, []);

  const removeDownload = useCallback((id: string) => {
    setActiveDownloads(prev => prev.filter(download => download.id !== id));
  }, []);

  const clearAllDownloads = useCallback(() => {
    setActiveDownloads([]);
  }, []);

  const hasActiveDownloads = activeDownloads.some(download => 
    download.status === 'initiated' || download.status === 'in_progress'
  );

  const value: DownloadProgressContextType = useMemo(() => ({
    activeDownloads,
    addDownload,
    updateProgress,
    completeDownload,
    failDownload,
    removeDownload,
    clearAllDownloads,
    hasActiveDownloads,
  }), [activeDownloads, addDownload, updateProgress, completeDownload, failDownload, removeDownload, clearAllDownloads, hasActiveDownloads]);

  return (
    <DownloadProgressContext.Provider value={value}>
      {children}
    </DownloadProgressContext.Provider>
  );
};
